/**
 * Section marks for the Auto Resolver handoff (What was done / What remains /
 * Notes / Next owner). Drawn here so the chat does not fall back to emoji.
 *
 * Colors follow the brand grammar: violet is Theo's record, orange is open
 * work, neutral is supporting text, brown is a person.
 */

import { BROWN, NEUTRAL, ORANGE, VIOLET } from "../../styles/brandTokens";

export type LedgerKind = "done" | "open" | "notes" | "handoff";

const TITLES: Record<string, LedgerKind> = {
  "what was done": "done",
  "what remains": "open",
  notes: "notes",
  "next owner / escalation": "handoff",
};

const COLOR: Record<LedgerKind, string> = {
  done: VIOLET.theoText,
  open: ORANGE.text,
  notes: NEUTRAL.textSecondary,
  handoff: BROWN.main,
};

const svg = (body: string): string =>
  `<svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true" focusable="false">${body}</svg>`;

/** 16px section marks. Done and remains share one square; the tick is the difference. */
const SECTION_MARK: Record<LedgerKind, string> = {
  done: svg(
    `<rect x="2" y="2" width="12" height="12" rx="2" stroke="currentColor" stroke-width="1.5"/>` +
      `<path d="M5.05 8.15 7.15 10.25 11.05 5.85" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>`
  ),
  open: svg(
    `<rect x="2" y="2" width="12" height="12" rx="2" stroke="currentColor" stroke-width="1.5"/>`
  ),
  notes: svg(
    `<rect x="1.75" y="2.85" width="3.3" height="3.3" rx="0.7" fill="currentColor"/>` +
      `<path d="M7 4.5h7.25" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>` +
      `<path d="M1.75 11.15h12.5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>`
  ),
  handoff: svg(
    `<rect x="1.25" y="4.85" width="5.4" height="5.4" rx="1.35" stroke="currentColor" stroke-width="1.5"/>` +
      `<circle cx="12.05" cy="4.45" r="1.7" stroke="currentColor" stroke-width="1.5"/>` +
      `<path d="M9.15 12.35c.45-1.95 1.4-2.9 2.9-2.9s2.45.95 2.9 2.9" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>`
  ),
};

const ROW_MARK: Record<LedgerKind, string> = {
  done: `<svg width="6" height="6" viewBox="0 0 6 6" aria-hidden="true" focusable="false"><rect width="6" height="6" rx="1" fill="currentColor"/></svg>`,
  open: `<svg width="6" height="6" viewBox="0 0 6 6" fill="none" aria-hidden="true" focusable="false"><rect x="0.75" y="0.75" width="4.5" height="4.5" rx="0.85" stroke="currentColor" stroke-width="1.2"/></svg>`,
  notes: `<svg width="6" height="6" viewBox="0 0 6 6" aria-hidden="true" focusable="false"><rect x="1.35" y="1.35" width="3.3" height="3.3" rx="0.6" fill="currentColor"/></svg>`,
  handoff: `<svg width="6" height="6" viewBox="0 0 6 6" aria-hidden="true" focusable="false"><rect x="1.35" y="1.35" width="3.3" height="3.3" rx="0.6" fill="currentColor"/></svg>`,
};

function ledgerKind(text: string): LedgerKind | null {
  const normalized = text
    .replace(/[\uFE0F\u200D]/g, "")
    .replace(/[\u2705\u23F3\u27A1\u{1F4DD}]/gu, "")
    .replace(/\s+/g, " ")
    .trim()
    .toLowerCase();
  return TITLES[normalized] ?? null;
}

function nextElement(node: Element): Element | null {
  let sibling = node.nextSibling;
  while (sibling) {
    if (sibling.nodeType === Node.ELEMENT_NODE) return sibling as Element;
    sibling = sibling.nextSibling;
  }
  return null;
}

function isFirstBlock(node: Element): boolean {
  let sibling = node.previousSibling;
  while (sibling) {
    if (sibling.nodeType === Node.ELEMENT_NODE) return false;
    if (sibling.nodeType === Node.TEXT_NODE && (sibling.textContent || "").trim()) return false;
    sibling = sibling.previousSibling;
  }
  return true;
}

function decorateHeading(doc: Document, paragraph: HTMLElement, kind: LedgerKind, isFirst: boolean): void {
  paragraph.classList.add("neo-ledger-heading");
  paragraph.setAttribute("data-ledger", kind);
  paragraph.setAttribute(
    "style",
    [
      "display:flex",
      "align-items:center",
      "gap:8px",
      `margin:${isFirst ? "0.05em" : "0.95em"} 0 0.28em`,
      "font-weight:600",
      "letter-spacing:-0.011em",
      `color:${NEUTRAL.text}`,
      "line-height:1.3",
    ].join(";")
  );
  paragraph.textContent = "";

  const mark = doc.createElement("span");
  mark.className = "neo-ledger-mark";
  mark.setAttribute("aria-hidden", "true");
  mark.setAttribute(
    "style",
    `display:inline-flex;flex:0 0 auto;width:16px;height:16px;min-width:16px;color:${COLOR[kind]}`
  );
  mark.innerHTML = SECTION_MARK[kind];

  const label = doc.createElement("span");
  label.textContent = titleFor(kind);

  paragraph.append(mark, label);
}

function titleFor(kind: LedgerKind): string {
  if (kind === "done") return "What was done";
  if (kind === "open") return "What remains";
  if (kind === "notes") return "Notes";
  return "Next owner / escalation";
}

function decorateList(doc: Document, list: HTMLElement, kind: LedgerKind): void {
  list.classList.add("neo-ledger-list");
  list.setAttribute("data-ledger", kind);
  // The chat bubble indents every list by 1.75em, which parks these marks under
  // the heading text. Pull them back so the mark sits just inside the 16px icon.
  list.setAttribute(
    "style",
    [
      "list-style:none !important",
      "margin:0.05em 0 0.35em !important",
      "margin-left:0 !important",
      "padding:0 !important",
      "padding-left:5px !important",
    ].join(";")
  );

  list.querySelectorAll(":scope > li").forEach((item) => {
    const li = item as HTMLElement;
    li.querySelectorAll('input[type="checkbox"]').forEach((box) => box.remove());
    li.classList.add("neo-ledger-row");
    li.setAttribute("style", "display:flex;align-items:flex-start;gap:8px;margin:0.22em 0;list-style:none;");

    const body = doc.createElement("span");
    body.className = "neo-ledger-row-body";
    body.setAttribute("style", "flex:1 1 auto;min-width:0;");
    while (li.firstChild) body.appendChild(li.firstChild);

    const mark = doc.createElement("span");
    mark.className = "neo-ledger-row-mark";
    mark.setAttribute("aria-hidden", "true");
    mark.setAttribute(
      "style",
      `display:inline-flex;flex:0 0 auto;width:6px;height:6px;min-width:6px;margin-top:0.48em;color:${COLOR[kind]}`
    );
    mark.innerHTML = ROW_MARK[kind];

    li.append(mark, body);
  });
}

/**
 * Replace emoji checklist headings in rendered chat HTML with the ledger marks.
 * A paragraph is a section only when its whole text is one of the four titles,
 * so ordinary sentences that mention "notes" are left alone. Already-styled
 * headings are skipped.
 */
export function styleResolverLedgerHtml(html: string): string {
  if (!html || typeof DOMParser === "undefined") return html;
  if (!/what was done|what remains|next owner|notes/i.test(html)) {
    return html;
  }

  const parser = new DOMParser();
  const doc = parser.parseFromString(`<div id="neo-ledger-root">${html}</div>`, "text/html");
  const root = doc.getElementById("neo-ledger-root");
  if (!root) return html;

  const headings = Array.from(root.querySelectorAll("p, h1, h2, h3, h4")).filter(
    (node) => !node.classList.contains("neo-ledger-heading") && ledgerKind(node.textContent || "")
  );
  if (!headings.length) return html;

  let styled = 0;
  headings.forEach((node) => {
    const kind = ledgerKind(node.textContent || "");
    if (!kind) return;
    const element = node as HTMLElement;
    decorateHeading(doc, element, kind, isFirstBlock(element));
    styled += 1;
    const list = nextElement(element);
    if (list && list.tagName === "UL") decorateList(doc, list as HTMLElement, kind);
  });

  return styled ? root.innerHTML : html;
}
